import { useRef, useState } from "react";
import useAnimateWhileVisible from "../../hooks/useAnimateWhileVisible";
import { Link } from "react-router-dom";
import "./CategoryRail.css";

// Minimum required correction (px) to bring `cardEl` fully inside
// `viewportEl`'s visible bounds, added on top of `currentShift`. Returns
// `currentShift` unchanged if the card is already fully visible — a
// centered card must not move. Shared by both pointer-hover and
// keyboard-focus activation so they behave identically.
const PADDING = 16;
export function correctionFor(cardEl, viewportEl, currentShift) {
  const vp = viewportEl.getBoundingClientRect();
  const card = cardEl.getBoundingClientRect();
  const leftBound = vp.left + PADDING;
  const rightBound = vp.right - PADDING;
  if (card.left >= leftBound && card.right <= rightBound) return currentShift;
  const delta = card.left < leftBound ? leftBound - card.left : rightBound - card.right;
  return currentShift + delta;
}

// True when (clientX, clientY) falls within `rect`. Used to tell a
// genuine pointer exit from the rail apart from a card merely shifting
// out from under a stationary cursor (see handleViewportPointerLeave).
// Missing/non-numeric coordinates are treated as "not inside" — if the
// cursor position can't be determined, resetting is the safer default
// over leaving the correction stuck on indefinitely. Exported so this
// decision can be unit-tested directly: jsdom's fireEvent doesn't
// populate clientX/clientY on synthesized pointer leave/enter events, so
// exercising this branch through the full event pipeline isn't possible
// in tests (it works correctly with real browser pointer events, where
// these coordinates are always populated).
export function cursorInsideRect(clientX, clientY, rect) {
  if (!rect || typeof clientX !== "number" || typeof clientY !== "number") return false;
  return clientX >= rect.left && clientX <= rect.right && clientY >= rect.top && clientY <= rect.bottom;
}

/**
 * Continuous right-to-left category marquee — pure CSS animation (no JS
 * timers/rAF) drives the constant motion, and animation-play-state handles
 * hover-pause/resume natively. The list is duplicated once so a track
 * translated by exactly -50% loops with no visible seam.
 *
 * On top of that, a small amount of JS state (`shift`) pulls whichever
 * card is currently hovered or keyboard-focused fully into view: the
 * marquee's position is purely time-based, so pausing it on hover/focus
 * can freeze a card mid-transition, partially outside the visible window.
 * `shift` is applied to a non-animated wrapper (.rail-focus-shift) around
 * .rail-track, since a running CSS animation always wins the cascade for
 * the property it animates — an inline transform on .rail-track itself
 * would be ignored.
 */
const CategoryRail = ({ categories }) => {
  const looped = [...categories, ...categories];
  const viewportRef = useRef(null);
  const pointerInsideRef = useRef(false);
  const focusInsideRef = useRef(false);
  const [shift, setShift] = useState(0);
  // The marquee runs only while it can be seen: offscreen or in a hidden
  // tab it is paused where it stands (speed Phase 0, 2026-10-08).
  const animate = useAnimateWhileVisible(viewportRef);

  // Under prefers-reduced-motion, CategoryRail.css disables the marquee
  // animation and makes .rail-viewport a real overflow-x: auto scroller —
  // the browser's own scroll-into-view-on-focus already fully solves
  // reachability there, so this correction would be redundant (and could
  // fight the native scroll position, since it's an additional transform
  // layered on top). window.matchMedia is absent in this project's jsdom
  // test setup, so the guard treats "can't tell" as "not reduced motion",
  // preserving today's behavior in tests.
  const prefersReducedMotion = () =>
    typeof window !== "undefined" &&
    typeof window.matchMedia === "function" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  const activate = (cardEl) => {
    if (prefersReducedMotion()) return;
    setShift((prev) => correctionFor(cardEl, viewportRef.current, prev));
  };

  const maybeReset = () => {
    if (!pointerInsideRef.current && !focusInsideRef.current) setShift(0);
  };

  const handleCardFocus = (e) => {
    focusInsideRef.current = true;
    activate(e.currentTarget);
  };

  const handleCardPointerEnter = (e) => {
    pointerInsideRef.current = true;
    activate(e.currentTarget);
  };

  // relatedTarget is null when focus leaves the document entirely (or, in
  // some environments, an object that isn't a Node at all) — guard with
  // instanceof before calling contains() so that case is treated as
  // "left", not as a crash.
  const isInsideViewport = (target) =>
    target instanceof Node && !!viewportRef.current?.contains(target);

  // Bound on the viewport, never on individual cards: shifting a card to
  // correct it moves it out from under a stationary cursor, which would
  // fire a spurious pointerleave on that card if listened to there.
  // Deliberately keyed off the pointer's own clientX/clientY against the
  // viewport's rect — which never moves — rather than relatedTarget: a
  // corrective shift moves a card (and everything nested under it) out
  // from under a stationary cursor, and relatedTarget-based ancestry
  // checks (as used for keyboard focus below, where they're reliable)
  // proved unreliable for synthesized pointer leave/enter events. Cursor
  // coordinates aren't affected by which element the DOM currently
  // considers "under" them, so this only resets on a genuine exit.
  const handleViewportPointerLeave = (e) => {
    if (cursorInsideRect(e.clientX, e.clientY, viewportRef.current?.getBoundingClientRect())) return;
    pointerInsideRef.current = false;
    maybeReset();
  };

  const handleViewportBlur = (e) => {
    if (isInsideViewport(e.relatedTarget)) return; // focus moved to another card in the rail
    focusInsideRef.current = false;
    maybeReset();
  };

  return (
    <div
      className={`rail-viewport${animate ? "" : " rail-viewport--paused"}`}
      ref={viewportRef}
      onPointerLeave={handleViewportPointerLeave}
      onBlur={handleViewportBlur}
    >
      <div
        className="rail-focus-shift"
        style={shift ? { transform: `translate3d(${shift}px, 0, 0)` } : undefined}
      >
        <div className="rail-track">
          {looped.map((c, i) => {
            const isReal = i < categories.length;
            return (
              <Link
                key={`${c.to}-${i}`}
                to={c.to}
                className="rail-card"
                aria-hidden={isReal ? undefined : "true"}
                tabIndex={isReal ? undefined : -1}
                onFocus={isReal ? handleCardFocus : undefined}
                onPointerEnter={isReal ? handleCardPointerEnter : undefined}
              >
                <span className="rail-icon" aria-hidden="true">{c.icon}</span>
                <span className="rail-label">{c.label}</span>
              </Link>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default CategoryRail;
